/**
 * Times for the logbook. The sailings are in Stockholm time: planned times are
 * local strings ("2026-07-01T10:00") and real times are UTC instants. Showing an
 * ISO instant by cutting out the clock would show UTC (an hour or two off), and
 * the day must change at Stockholm midnight, not UTC midnight.
 */

export const TIME_ZONE = "Europe/Stockholm";

/** The local calendar day (YYYY-MM-DD) of an ISO instant. */
export const localDay = (iso: string, timeZone = TIME_ZONE): string =>
  new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));

/** The clock time (HH:MM) of an ISO instant in Stockholm; empty when there is none. */
export const stockholmTime = (iso?: string, timeZone = TIME_ZONE): string =>
  iso ? new Intl.DateTimeFormat("sv-SE", { timeZone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(iso)) : "";

/** When an entry happened: the time the skipper gave, otherwise when it was recorded. */
export const entryTime = (e: { signedAt: string; data?: Record<string, unknown> }): string =>
  typeof e.data?.occurredAt === "string" ? e.data.occurredAt : e.signedAt;

/** Was the entry recorded more than five minutes after it happened? */
export const recordedLate = (e: { signedAt: string; data?: Record<string, unknown> }): boolean =>
  Date.parse(e.signedAt) - Date.parse(entryTime(e)) > 5 * 60 * 1000;

/** The clock part of a planned local time string ("2026-07-01T10:00" gives "10:00"). */
export const plannedTime = (local?: string): string => (local ? local.slice(11, 16) : "");

/** Today in Stockholm. */
export const todayLocal = (now = new Date()): string => localDay(now.toISOString());

/** The local day `n` days from today (Stockholm), as YYYY-MM-DD. */
export function plusDaysLocal(n: number, now = new Date()): string {
  const [y, m, d] = todayLocal(now).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}
